import { logger, maskUserId, maskToken, maskPermissions } from "@/utils/logger";
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { basicInfoService } from "@/services";
import { normalizePermissions } from "@/utils/permissionMap";
import { httpApi } from "@/utils/http";
import type { RouteLocationNormalized } from "vue-router";
import type { BasicInfoData } from "@/services/basicInfo";

export interface User {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  roles: string[];
  permissions: string[];
  lastLoginTime?: string;
}

export interface TraderInfo {
  futureetf_traders: string[];
  etf_arbitragers?: string[]; // ETF 套利机器人（折溢价套利 WebSocket 用）
  websocket_url?: string; // WebSocket 服务器地址
  etf_tickers: string[];
  future_tickers: string[];
  pcp_option_pair: string[];
  pcp_traders?: string[];
  pcp_multi_traders?: string[]; // PCP 多账户交易员列表
  convert_bond_arbitrager?: string[]; // 转债套利交易员列表
  dual_leg_traders?: string[]; // 双腿价差交易员列表
  hedged_convert_traders?: string[]; // 对冲申赎触发器列表
  etf_t0_traders?: string[]; // ETF T+0 交易员列表
  operators: string[];
  permission: string;
  user_type: string;
  funds_to_monitor: string[];
  fund_info: Record<string, unknown>[];
}

export const useUserStore = defineStore("user", () => {
  // 状态
  const currentUser = ref<User>({
    id: "",
    username: "",
    roles: [],
    permissions: [],
  });

  const traderInfo = ref<TraderInfo>({
    futureetf_traders: [],
    etf_arbitragers: [],
    websocket_url: "",
    etf_tickers: [],
    future_tickers: [],
    pcp_option_pair: [],
    dual_leg_traders: [], // 双腿价差交易员
    hedged_convert_traders: [], // 对冲申赎触发器
    etf_t0_traders: [], // ETF T+0 交易员
    operators: [],
    permission: "",
    user_type: "user",
    funds_to_monitor: [],
    fund_info: [],
  });

  // 全局 ETF/期货代码列表（来自 /api/futureetftickers/，带 1 天 localStorage 缓存）
  const futureETFTickers = ref<{ etf_tickers: string[]; future_tickers: string[] }>({
    etf_tickers: [],
    future_tickers: [],
  });

  const isAuthenticated = ref(false);
  const token = ref("");
  const basicInfo = ref<BasicInfoData | null>(null);
  const basicInfoLoading = ref(false);
  const basicInfoProgress = ref<{ completed: number; total: number; label: string }>({
    completed: 0,
    total: 2, // 优化后只有2个必需步骤：TraderInfo + Funds
    label: "",
  });

  const normalizedUserPermissions = computed(() => {
    return new Set(normalizePermissions(currentUser.value.permissions || []));
  });

  const rawUserPermissions = computed(() => {
    return new Set((currentUser.value.permissions || []).map((p) => String(p).trim()));
  });

  // 计算属性
  const hasPermission = computed(() => {
    return (permission: string | string[]) => {
      const requiredList = Array.isArray(permission) ? permission : [permission];
      const normalizedRequired = normalizePermissions(requiredList);
      if (normalizedRequired.length === 0) return false;
      const userPerms = normalizedUserPermissions.value;
      return (
        normalizedRequired.some((key) => userPerms.has(key)) ||
        currentUser.value.roles.includes("admin")
      );
    };
  });

  const hasRole = computed(() => {
    return (role: string) => {
      return currentUser.value.roles.includes(role);
    };
  });

  // 路由权限检查
  const canAccessRoute = computed(() => {
    return (route: RouteLocationNormalized) => {
      const requiredPermissions = route.meta?.permissions as string[];
      const requiredRoles = route.meta?.roles as string[];

      if (route.meta?.exactPermissions) {
        return !!requiredPermissions?.length && requiredPermissions.every(permission => rawUserPermissions.value.has(permission));
      }

      // Default-Deny：未配置权限的路由仅白名单放行
      if (!requiredPermissions && !requiredRoles) {
        const noPermissionPaths = ["/home/landing", "/home/copilot", "/home/empty"];
        return noPermissionPaths.includes(route.path);
      }

      if (requiredPermissions && requiredPermissions.length > 0) {
        // 1. 精确匹配（使用normalize后的权限）
        const normalizedRequired = normalizePermissions(requiredPermissions);
        if (
          normalizedRequired.some((permission) => normalizedUserPermissions.value.has(permission))
        ) {
          return true;
        }

        // 2. 精确匹配（使用原始权限）
        if (requiredPermissions.some((permission) => rawUserPermissions.value.has(permission))) {
          return true;
        }

        // 3. 模糊匹配（支持 xxx_read_update 包含 xxx_read 的情况）
        const userPermArray = Array.from(rawUserPermissions.value);
        if (
          requiredPermissions.some((requiredPerm) =>
            userPermArray.some((userPerm) =>
              userPerm.toLowerCase().includes(requiredPerm.toLowerCase()),
            ),
          )
        ) {
          return true;
        }
      }

      if (requiredRoles?.some((role) => hasRole.value(role))) {
        return true;
      }

      return false;
    };
  });

  // 暴露给组件使用的user引用
  const user = computed(() => currentUser.value);

  // 方法
  const setUser = (user: Partial<User>) => {
    currentUser.value = { ...currentUser.value, ...user };
  };

  const setTraderInfo = (info: Partial<TraderInfo>) => {
    traderInfo.value = { ...traderInfo.value, ...info };
  };

  const setBasicInfo = (info: BasicInfoData | null) => {
    basicInfo.value = info;
  };

  const setAuthenticated = (value: boolean) => {
    isAuthenticated.value = value;
  };

  const setToken = (newToken: string) => {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
  };

  const setPermissions = (perms: string[]) => {
    currentUser.value.permissions = normalizePermissions(perms);
  };

  // 获取基础信息
  const fetchBasicInfo = async (useCache: boolean = true): Promise<BasicInfoData | null> => {
    // 获取当前用户ID
    const userId = currentUser.value.username || currentUser.value.id;
    if (!userId) {
      logger.warn("无法获取基础信息：用户未登录");
      return null;
    }

    // 如果允许使用缓存，先检查缓存
    if (useCache) {
      const cached = basicInfoService.getCachedBasicInfo(userId);
      if (cached) {
        logger.debug("使用缓存的基础信息");
        setBasicInfo(cached);
        // 用缓存的信息更新traderInfo，同时将funds同步到fund_info。
        // 注意: 缓存里没有 traderInfo 时也必须把 funds 灌进 fund_info —— 否则
        // ensureBasicInfo() 这类"fund_info 为空就补一次"的自愈会静默失效。
        if (cached.traderInfo) {
          const updatedTraderInfo = {
            ...cached.traderInfo,
            fund_info: cached.funds || [],
          };
          setTraderInfo(updatedTraderInfo);
        } else if (cached.funds?.length) {
          setTraderInfo({ fund_info: cached.funds });
        }
        return cached;
      }
    }

    basicInfoLoading.value = true;
    basicInfoProgress.value = { completed: 0, total: 2, label: "开始加载" }; // 优化后只有2个步骤
    try {
      logger.debug("开始获取基础信息", { userId: maskUserId(userId) });
      const basicInfoData = await basicInfoService.fetchAllBasicInfo(userId, (update) => {
        basicInfoProgress.value = update;
      });

      setBasicInfo(basicInfoData);

      // 更新traderInfo store中的数据
      if (basicInfoData.traderInfo) {
        // 将funds数据同步到traderInfo.fund_info中，供DialogCreateLogic等组件使用
        const updatedTraderInfo = {
          ...basicInfoData.traderInfo,
          fund_info: basicInfoData.funds || [],
        };
        setTraderInfo(updatedTraderInfo);
        logger.debug("基础信息已更新到用户store", { fundCount: basicInfoData.funds?.length || 0 });
      }

      return basicInfoData;
    } catch (error) {
      logger.error("获取基础信息失败:", error);

      // 显示错误信息并提示重新登录
      const errorMessage = error instanceof Error ? error.message : "未知错误";

      // 动态导入Element Plus消息框，避免循环依赖
      try {
        const { ElMessageBox } = await import("element-plus");
        await ElMessageBox.alert(
          `${errorMessage}\n\n请重新登录以获取最新数据。`,
          "系统配置加载失败",
          {
            confirmButtonText: "重新登录",
            type: "error",
          },
        );

        // 清除当前用户状态并跳转到登录页
        logout();

        const { default: router } = await import("@/router");
        router.push("/login");
      } catch (importError) {
        logger.error("显示错误弹框失败:", importError);
        // 降级处理：直接登出
        logout();
      }

      return null;
    } finally {
      basicInfoLoading.value = false;
    }
  };

  // 🆕 新增：后台异步加载基础信息（不阻塞登录流程）
  const fetchBasicInfoAsync = async () => {
    try {
      const userId = currentUser.value.username || currentUser.value.id;
      if (!userId) {
        logger.warn("无法后台加载基础信息：用户未登录");
        return;
      }

      logger.debug("开始后台加载基础信息（不阻塞登录）");
      await fetchBasicInfo(true);
      logger.debug("后台加载基础信息成功");
    } catch (error) {
      logger.warn("后台加载基础信息失败（不影响登录）:", error);
    }
  };

  // 🆕 仅异步加载funds信息（避免重复调用fetchTraderInfo）
  const fetchFundsOnlyAsync = async () => {
    try {
      logger.debug("开始后台加载funds信息（不阻塞登录）");
      const fundsData = await basicInfoService.fetchFunds();

      // 更新basicInfo中的funds
      if (basicInfo.value) {
        basicInfo.value.funds = fundsData;
      } else {
        basicInfo.value = {
          traderInfo: traderInfo.value,
          funds: fundsData,
          futureETFTickers: null,
          lastUpdated: new Date().toISOString(),
        };
      }

      // 同步到traderInfo.fund_info
      if (traderInfo.value) {
        traderInfo.value.fund_info = fundsData;
        // 必须回写 localStorage: setCurrentUser 里的 saveToStorage() 发生在本函数之前,
        // 存进去的是空 fund_info。不回写的话下次冷启动恢复出来的仍是空表。
        saveToStorage();
      }

      logger.debug("后台加载funds成功");
    } catch (error) {
      logger.warn("后台加载funds失败（不影响登录）:", error);
    }
  };

  /**
   * 加载全局 ETF/期货代码列表（/api/futureetftickers/）
   * - 优先从 localStorage 读取（1天 TTL）
   * - 失败时 fallback 到 traderInfo 中的用户级数据
   */
  const FUTURE_ETF_CACHE_KEY = "futureetf_tickers_cache";
  const FUTURE_ETF_CACHE_TTL = 24 * 60 * 60 * 1000; // 1天

  const fetchFutureETFTickers = async () => {
    try {
      // 1. 检查 localStorage 缓存
      const cached = localStorage.getItem(FUTURE_ETF_CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && Date.now() - parsed.timestamp < FUTURE_ETF_CACHE_TTL) {
            futureETFTickers.value = {
              etf_tickers: parsed.etf_tickers || [],
              future_tickers: parsed.future_tickers || [],
            };
            logger.debug("futureETFTickers: 使用 localStorage 缓存", {
              etf: parsed.etf_tickers?.length,
              future: parsed.future_tickers?.length,
            });
            return;
          }
        } catch {
          // 缓存损坏，继续请求接口
        }
      }

      // 2. 调用接口
      const data = await basicInfoService.fetchFutureETFTickers();
      futureETFTickers.value = {
        etf_tickers: data.etf_tickers || [],
        future_tickers: data.future_tickers || [],
      };

      // 3. 写入 localStorage（带时间戳）
      localStorage.setItem(
        FUTURE_ETF_CACHE_KEY,
        JSON.stringify({
          etf_tickers: data.etf_tickers || [],
          future_tickers: data.future_tickers || [],
          timestamp: Date.now(),
        }),
      );

      logger.debug("futureETFTickers: 从接口加载成功", {
        etf: data.etf_tickers?.length,
        future: data.future_tickers?.length,
      });
    } catch (error) {
      // 4. 失败时 fallback 到 traderInfo 的用户级数据
      logger.warn("futureETFTickers: 接口加载失败，fallback 到 traderInfo", error);
      futureETFTickers.value = {
        etf_tickers: traderInfo.value?.etf_tickers || [],
        future_tickers: traderInfo.value?.future_tickers || [],
      };
    }
  };

  // 设置当前用户信息（与原系统兼容）
  const setCurrentUser = async (userInfo: { username: string; token: string; expires: number }) => {
    try {
      // 🆕 Step 1: 从后端API获取用户信息（包含权限）
      let permissions: string[] = [];
      let traderInfoFromApi: any = null;

      try {
        traderInfoFromApi = await basicInfoService.fetchTraderInfo(userInfo.username);

        // 🆕 Step 2: 从API响应中提取权限
        if (traderInfoFromApi.permissions && Array.isArray(traderInfoFromApi.permissions)) {
          // 优先使用新格式（数组）
          permissions = traderInfoFromApi.permissions;
          logger.debug("从API获取权限（数组格式）", { count: permissions.length });
        } else if (traderInfoFromApi.permission) {
          // 兼容旧格式（字符串），转换为数组
          permissions = traderInfoFromApi.permission
            .split(";")
            .map((p: string) => p.trim())
            .filter(Boolean);
          logger.debug("从API获取权限（字符串格式）", { count: permissions.length });
        } else {
          // 没有权限时使用默认权限
          permissions = ["report_daily_review_plan"];
          logger.warn("⚠️ 用户无权限配置，使用默认权限:", permissions);
        }
      } catch (error) {
        logger.error("❌ 从后端获取权限失败:", error);
        // 使用默认最小权限
        permissions = ["report_daily_review_plan"];
        logger.warn("⚠️ 权限获取失败，使用默认权限:", permissions);
      }

      // Step 3: 创建用户对象
      const user: User = {
        id: userInfo.username,
        username: userInfo.username,
        email: userInfo.username,
        roles: traderInfoFromApi?.user_type === "admin" ? ["admin", "trader"] : ["trader"],
        permissions: permissions, // ✅ 从API获取
        lastLoginTime: new Date().toISOString(),
      };

      // Step 4: 设置交易员信息
      const defaultTraderInfo: TraderInfo = {
        futureetf_traders: traderInfoFromApi?.futureetf_traders || [],
        etf_arbitragers: traderInfoFromApi?.etf_arbitragers || [], // ETF 套利机器人
        websocket_url: traderInfoFromApi?.websocket_url || "", // WebSocket 服务器地址
        etf_tickers: traderInfoFromApi?.etf_tickers || [],
        future_tickers: traderInfoFromApi?.future_tickers || [],
        pcp_option_pair: traderInfoFromApi?.pcp_option_pair || [],
        pcp_multi_traders: traderInfoFromApi?.pcp_multi_traders || [],
        convert_bond_arbitrager: traderInfoFromApi?.convert_bond_arbitrager || [],
        dual_leg_traders: traderInfoFromApi?.dual_leg_traders || [], // 双腿价差交易员
        hedged_convert_traders: traderInfoFromApi?.hedged_convert_traders || [], // 对冲申赎触发器
        etf_t0_traders: traderInfoFromApi?.etf_t0_traders || [], // ETF T+0 交易员
        operators: traderInfoFromApi?.operators || [userInfo.username],
        permission: user.permissions.join(";"), // 转换为字符串（兼容老系统）
        user_type: traderInfoFromApi?.user_type || "user",
        funds_to_monitor: traderInfoFromApi?.funds_to_monitor || [],
        // ⚠️ 不要用空数组覆盖已加载的 fund_info。本函数在每次刷新/会话恢复时都会重跑,
        // 而 fund_info 是后面 fetchFundsOnlyAsync() 异步补的(fire-and-forget)。一旦它
        // 失败或晚于消费方到达, 空数组会让 OrderBlotter 的 hostMeta 整张表失效:
        // 基金/账户两列全空, 且 WS 帧因 monitoredHosts 为空被 processWsMessage 全部丢弃
        // (2026-09-22 委托明细页现场: 有数据、显示已连接, 但基金/账户空白且不再更新)。
        fund_info: traderInfo.value?.fund_info?.length ? traderInfo.value.fund_info : [],
      };

      // Step 5: 保存到Store
      setUser(user);
      setTraderInfo(defaultTraderInfo);
      setToken(userInfo.token);
      setAuthenticated(true);

      // Step 6: 保存权限到Cookie
      try {
        const { TokenManager } = await import("@/utils/auth");
        TokenManager.savePermissions(user.permissions, 30);
        logger.debug("权限已保存到Cookie", { count: user.permissions.length });
      } catch (error) {
        logger.warn("⚠️ 保存权限到Cookie失败:", error);
      }

      saveToStorage();

      // ✅ Step 7: 改为后台异步加载基础信息（不阻塞登录流程）
      // 🔧 修复：由于已经在Step 1获取了traderInfo，只需要异步加载funds即可
      // 避免重复调用fetchTraderInfo导致超时
      if (traderInfoFromApi) {
        // 已经有traderInfo，只需要加载funds
        fetchFundsOnlyAsync();
      } else {
        // 如果获取traderInfo失败，再尝试完整加载
        fetchBasicInfoAsync();
      }

      logger.info("✅ 用户登录成功（基础信息正在后台加载）");
      return { success: true, user };
    } catch (error) {
      logger.error("❌ 设置用户信息失败:", error);
      return { success: false, error: "设置用户信息失败" };
    }
  };

  const login = async (credentials: { username: string; password: string }) => {
    try {
      // 真实登录API调用
      const result = await httpApi.post("/token/", {
        username: credentials.username,
        password: credentials.password,
      });

      if (result && result.refresh) {
        // 动态导入避免循环依赖
        const { TokenManager } = await import("@/utils/auth");
        TokenManager.saveCredentials(credentials.username, result.refresh, 30);

        await setCurrentUser({
          username: credentials.username,
          token: result.refresh,
          expires: 30,
        });

        return { success: true, user: currentUser.value };
      }

      throw new Error("登录返回数据格式错误");
    } catch (error) {
      logger.error("Login failed:", error);
      return { success: false, error: error instanceof Error ? error.message : "Login failed" };
    }
  };

  const logout = () => {
    logger.info("用户退出登录");

    // 保存userId用于清理缓存（在清空currentUser之前）
    const userId = currentUser.value.username || currentUser.value.id;

    // 1. 清空用户状态

    setUser({
      id: "",
      username: "",
      roles: [],
      permissions: [],
    });
    setTraderInfo({
      futureetf_traders: [],
      etf_arbitragers: [],
      websocket_url: "",
      etf_tickers: [],
      future_tickers: [],
      pcp_option_pair: [],
      dual_leg_traders: [], // 双腿价差交易员
      hedged_convert_traders: [], // 对冲申赎触发器
      etf_t0_traders: [], // ETF T+0 交易员
      operators: [],
      permission: "",
      user_type: "user",
      funds_to_monitor: [],
      fund_info: [],
    });
    setToken("");
    setAuthenticated(false);
    setBasicInfo(null);

    // 2. 清除localStorage（包含所有权限数据）

    localStorage.clear();

    // 3. 清除sessionStorage

    sessionStorage.clear();

    // 4. 清除基础信息缓存

    try {
      if (userId) {
        basicInfoService.clearCache(userId);
        logger.log("   ✅ 基础信息缓存已清除");
      }
    } catch (error) {
      logger.warn("   ⚠️ 清除基础信息缓存失败:", error);
    }

    // 5. 清除Cookie中的权限和凭据

    try {
      import("@/utils/auth").then(({ TokenManager }) => {
        TokenManager.clearCredentials();
        logger.log("   ✅ Cookie凭据已清除");
      });
    } catch (error) {
      logger.warn("   ⚠️ 清除Cookie失败:", error);
    }

    // 6. 清除IndexedDB缓存（如果有的话）

    try {
      if (window.indexedDB) {
        indexedDB
          .databases()
          .then((databases) => {
            databases.forEach((db) => {
              if (db.name) {
                indexedDB.deleteDatabase(db.name);
                logger.log(`   ✅ 已删除数据库: ${db.name}`);
              }
            });
          })
          .catch((error) => {
            logger.warn("   ⚠️ 清除IndexedDB失败:", error);
          });
      }
    } catch (error) {
      logger.warn("   ⚠️ IndexedDB清理失败:", error);
    }

    logger.log("✅ 退出登录完成，所有缓存已清理");
  };

  const refreshToken = async () => {
    try {
      // 使用当前刷新token获取新的访问token
      const currentRefreshToken = token.value;
      if (!currentRefreshToken) {
        throw new Error("没有可用的刷新token");
      }

      const result = await httpApi.post("/token/refresh/", {
        refresh: currentRefreshToken,
      });

      if (result && result.access) {
        setToken(result.access);
        return result.access;
      }

      throw new Error("刷新token返回数据格式错误");
    } catch (error) {
      logger.error("Token refresh failed:", error);
      logout();
      throw error;
    }
  };

  // 从后端API刷新权限（优先），降级到Cookie
  const fetchPermissions = async () => {
    try {
      const username = currentUser.value.username;
      if (!username) {
        logger.warn("⚠️ 无法刷新权限：用户未登录");
        return currentUser.value.permissions;
      }

      // 🆕 从后端API刷新权限
      const traderInfo = await basicInfoService.fetchTraderInfo(username);

      let permissions: string[] = [];
      if (traderInfo.permissions && Array.isArray(traderInfo.permissions)) {
        // 优先使用新格式（数组）
        permissions = traderInfo.permissions;
      } else if (traderInfo.permission) {
        // 兼容旧格式（字符串），转换为数组
        permissions = traderInfo.permission
          .split(";")
          .map((p) => p.trim())
          .filter(Boolean);
      } else {
        permissions = ["report_daily_review_plan"];
      }

      // 更新Store
      setPermissions(permissions);

      // 更新Cookie
      try {
        const { TokenManager } = await import("@/utils/auth");
        TokenManager.savePermissions(permissions, 30);
        logger.debug("权限已从API刷新", { count: permissions.length });
      } catch (error) {
        logger.warn("⚠️ 保存权限到Cookie失败:", error);
      }

      saveToStorage();
      return permissions;
    } catch (error) {
      logger.error("❌ 从API刷新权限失败:", error);

      // 降级方案：从Cookie读取
      try {
        const { TokenManager } = await import("@/utils/auth");
        const permissionsFromCookie = TokenManager.getPermissions();

        if (permissionsFromCookie && permissionsFromCookie.length > 0) {
          logger.debug("从Cookie获取权限（降级方案）", { count: permissionsFromCookie.length });
          setPermissions(permissionsFromCookie);
          return permissionsFromCookie;
        }
      } catch (e) {
        logger.error("❌ 从Cookie读取权限失败:", e);
      }

      // 最终降级：使用当前权限或默认权限
      return currentUser.value.permissions.length > 0
        ? currentUser.value.permissions
        : ["report_daily_review_plan"];
    }
  };

  const initFromStorage = async () => {
    // 🔧 修复：防止重复调用API导致401错误和networkidle超时
    // 如果用户已经认证，直接返回，避免重复调用setCurrentUser触发API
    if (isAuthenticated.value && currentUser.value.username) {
      logger.debug("用户已认证，跳过initFromStorage");
      return;
    }

    // 优先从Cookie读取（与原系统兼容）
    try {
      const authModule = await import("@/utils/auth");
      const username = authModule.TokenManager.getUsername();
      const token = authModule.TokenManager.getToken();

      if (username && token) {
        logger.debug("从Cookie恢复用户状态");
        await setCurrentUser({ username, token, expires: 30 });
        return;
      }
    } catch (error) {
      logger.log("Cookie认证失败，尝试localStorage:", error);
    }

    // 备选方案：从localStorage读取
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    const savedTraderInfo = localStorage.getItem("traderInfo");

    if (savedToken) {
      setToken(savedToken);
      setAuthenticated(true);
    }

    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setUser(user);
        // 规范化历史缓存中的权限键，避免与路由不匹配
        setPermissions(user?.permissions || []);
      } catch (error) {
        logger.error("Failed to parse saved user:", error);
      }
    }

    if (savedTraderInfo) {
      try {
        const info = JSON.parse(savedTraderInfo);
        setTraderInfo(info);
      } catch (error) {
        logger.error("Failed to parse saved trader info:", error);
      }
    }

    if (savedToken && savedUser && currentUser.value.username) {
      logger.debug("从localStorage恢复用户状态");
    }
  };

  const saveToStorage = () => {
    localStorage.setItem("user", JSON.stringify(currentUser.value));
    localStorage.setItem("traderInfo", JSON.stringify(traderInfo.value));
  };

  return {
    // 状态
    currentUser,
    user, // 暴露给组件的user引用
    traderInfo,
    futureETFTickers,
    isAuthenticated,
    token,
    basicInfo,
    basicInfoLoading,
    basicInfoProgress,

    // 计算属性
    hasPermission,
    hasRole,
    canAccessRoute,

    // 方法
    setUser,
    setTraderInfo,
    setBasicInfo,
    setAuthenticated,
    setToken,
    setCurrentUser,
    fetchBasicInfo,
    fetchFutureETFTickers,
    login,
    logout,
    refreshToken,
    initFromStorage,
    saveToStorage,
    setPermissions,
    fetchPermissions,
  };
});
